import React from 'react';
import { Navigate, Route } from 'react-router-dom';
import { createApp } from '@backstage/app-defaults';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import {
  AlertDisplay,
  OAuthRequestDialog,
} from '@backstage/core-components';
import { CatalogIndexPage, CatalogEntityPage } from '@backstage/plugin-catalog';
import { SearchPage } from '@backstage/plugin-search';
import { TechDocsIndexPage, TechDocsReaderPage } from '@backstage/plugin-techdocs';
import { UserSettingsPage } from '@backstage/plugin-user-settings';

import { UnifiedThemeProvider } from '@backstage/theme';

import { apis } from './apis';
import { Root } from './components/Root';
import { BackendRequiredPage } from './components/BackendRequiredPage';
import { saSreLightTheme, saSreDarkTheme } from './themes/saSreTheme';

// SA_SRE Plugins
import {
  SaSreChatPage,
  SaSreChatWidget,
} from '@internal/plugin-sa-sre-chat';

import {
  SreDashboardPage,
  GovernancePage,
  ResourcesPage,
  DecisionsPage,
} from '@internal/plugin-sre-dashboard';

const app = createApp({
  apis,
  themes: [
    {
      id: 'sa-sre-light',
      title: 'SA_SRE Light',
      variant: 'light',
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={saSreLightTheme} children={children} />
      ),
    },
    {
      id: 'sa-sre-dark',
      title: 'SA_SRE Dark',
      variant: 'dark',
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={saSreDarkTheme} children={children} />
      ),
    },
  ],
});

/**
 * App - Root component for the Super Cloud SRE Portal.
 *
 * Integrates Backstage core features with SA_SRE custom plugins:
 * - Service Catalog (catalog)
 * - SRE Dashboard (sre-dashboard) — 8-tab operations dashboard
 * - SRE Chat (sre-chat) — both full-page route and floating widget
 * - Governance (governance) — roles, audit log, changes (FT-010)
 * - Resources (resources) — ON/OFF management (FT-008)
 * - Decisions (decisions) — investigation history (FT-009)
 * - TechDocs (docs)
 * - Search (search)
 */
export default app.createRoot(
  <>
    <AlertDisplay transientTimeoutMs={2500} />
    <OAuthRequestDialog />
    <AppRouter>
      <Root>
        <FlatRoutes>
          {/* Home */}
          <Route path="/" element={<Navigate to="/sre-dashboard/overview" replace />} />

          {/* Service Catalog — バックエンド必須 */}
          <Route
            path="/catalog"
            element={
              <BackendRequiredPage feature="サービスカタログ">
                <CatalogIndexPage />
              </BackendRequiredPage>
            }
          />
          <Route
            path="/catalog/:namespace/:kind/:name"
            element={
              <BackendRequiredPage feature="サービスカタログ">
                <CatalogEntityPage />
              </BackendRequiredPage>
            }
          />

          {/* SRE Dashboard (8-tab operations dashboard) */}
          <Route path="/sre-dashboard/*" element={<SreDashboardPage />} />

          {/* SRE Chat (full-page mode) */}
          <Route path="/sre-chat" element={<SaSreChatPage />} />

          {/* Governance (FT-010) */}
          <Route path="/governance/*" element={<GovernancePage />} />

          {/* Resources (FT-008) */}
          <Route path="/resources" element={<ResourcesPage />} />

          {/* Decisions (FT-009) */}
          <Route path="/decisions" element={<DecisionsPage />} />

          {/* TechDocs — バックエンド必須 */}
          <Route
            path="/docs"
            element={
              <BackendRequiredPage feature="技術ドキュメント">
                <TechDocsIndexPage />
              </BackendRequiredPage>
            }
          />
          <Route
            path="/docs/:namespace/:kind/:name/*"
            element={
              <BackendRequiredPage feature="技術ドキュメント">
                <TechDocsReaderPage />
              </BackendRequiredPage>
            }
          />

          {/* Search — バックエンド必須 */}
          <Route
            path="/search"
            element={
              <BackendRequiredPage feature="検索">
                <SearchPage />
              </BackendRequiredPage>
            }
          />

          {/* Settings */}
          <Route path="/settings" element={<UserSettingsPage />} />
        </FlatRoutes>
      </Root>

      {/* Floating Chat Widget — persists across all pages */}
      <SaSreChatWidget />
    </AppRouter>
  </>,
);
